import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    InfraAresta,
    InfraFaixa,
    InfraNo,
    STEP_CONCLUIDO,
    STEP_ERRO,
    STEP_EXECUTANDO,
    STEP_PULADO,
    rotuloDestino
} from '../../models/InfraPipeline';
import { duracao } from '../../models/InfraRun';

const LARGURA_NO = 232;
const ALTURA_NO = 104;
const GAP_COLUNA = 96;
const GAP_LINHA = 36;
const MARGEM = 24;
// Folga que um fio de passagem guarda para nao encostar num card.
const GAP_FIO = 22;
// Quadro de conta: respiro antes dele, altura do titulo e folga interna.
const GAP_GRUPO = 46;
const ALTURA_GRUPO = 30;
const PAD_GRUPO = 16;
// Calha a esquerda de tudo, por onde as linhas de retorno descem. Os cards
// comecam depois dela, e por isso nenhuma volta cruza card: ela desce por fora.
// Ate onde uma linha de blocos cresce antes de quebrar para a de baixo. Seis
// cards de largura e o que cabe numa tela sem obrigar a afastar o zoom.
const LARGURA_MAXIMA = 6 * (232 + 96);
// Respiro entre uma linha de blocos e a seguinte. Precisa caber o corredor por
// onde as ligacoes longas andam.
// 108 e nao menos: e por aqui que passam todas as voltas de uma linha de bloco
// para a seguinte, e num deploy sao dez fios no mesmo vao.
const GAP_LINHA_BLOCO = 108;
// A calha guarda dois espacos: os trilhos verticais das voltas, colados na
// margem, e depois deles a faixa onde as entradas descem para os cards.
const CALHA = 130;
// Trilhos dentro da calha, para linhas paralelas nao virarem uma so. Oito
// porque o build sozinho abre sete arestas, e duas delas dividindo trilho ja
// voltariam a parecer uma.
const CALHA_TRILHOS = 8;
const CALHA_PASSO = 8;
// Distancia entre dois fios que andariam juntos. Sete arestas saem do build:
// sem afastar uma da outra elas viram um traco so, e o desenho passa a mentir
// sobre quantas coisas dependem daquele passo.
const ESPACO_FIO = 8;
// Quanto a linha avanca em linha reta antes de virar, e o raio do canto.
// 26 e nao menos: a entrada tem de cair a esquerda da borda do quadro da
// conta, senao a linha encosta nela.
const VOLTA_FOLGA = 26;
const VOLTA_RAIO = 12;

const ZOOM_MIN = 0.35;
const ZOOM_MAX = 2;
const ARRASTO_MINIMO = 4;

interface Posicao {
    x: number;
    y: number;
}

interface Vista {
    x: number;
    y: number;
    z: number;
}

/** Um bloco ja medido, antes de saber onde ele cai na tela. */
interface Bloco {
    chave: string;
    rotulo: string;
    ligado: boolean;
    nos: Array<{ no: InfraNo; coluna: number; linha: number }>;
    colunas: number;
    linhas: number;
}

/** O retangulo de um bloco, com o titulo em cima. */
interface Quadro {
    chave: string;
    rotulo: string;
    ligado: boolean;
    x: number;
    largura: number;
    y: number;
    altura: number;
}

interface Props {
    nos: Array<InfraNo>;
    arestas: Array<InfraAresta>;
    /** Vazio desenha o DAG solto; com faixas, desenha em quadros e linhas. */
    faixas: Array<InfraFaixa>;
    selecionado: string | null;
    onSelecionar: (chave: string) => void;
}

const limitar = (valor: number, minimo: number, maximo: number): number =>
    Math.min(maximo, Math.max(minimo, valor));

/**
 * Desenha o DAG do pipeline num canvas que se move.
 *
 * As arestas ficam num SVG ao fundo e os nos sao divs posicionados em cima.
 * Fazer o no em SVG obrigaria a remontar texto, badge e botao em <text> e
 * <rect>; do jeito que esta, o no e HTML normal e herda o CSS do CoreUI.
 *
 * A navegacao e a de editor de fluxo: arrasta para mover e roda para zoom, com
 * shift+roda deslocando na horizontal. Nada de barra de rolagem, que num DAG
 * largo escondia metade do desenho e obrigava a caçar o passo.
 */
const FlowGraph = (props: Props) => {
    const { nos, arestas, faixas, selecionado, onSelecionar } = props;

    const viewportRef = useRef<HTMLDivElement>(null);
    const [vista, setVista] = useState<Vista>({ x: 0, y: 0, z: 1 });
    const [arrastando, setArrastando] = useState<boolean>(false);

    const inicioRef = useRef<{ mouseX: number; mouseY: number; panX: number; panY: number } | null>(null);
    const arrastouRef = useRef<boolean>(false);

    /**
     * Layout em faixas, para fluxo que ramifica por destino.
     *
     * O tronco fica numa faixa em cima e cada ramo ganha a sua embaixo, com o
     * nome ao lado. E diferente do empacotamento generico de proposito: ali a
     * posicao sai da topologia, e um ramo acaba no meio de outro; aqui a faixa
     * e a unidade de leitura, e olhar o desenho ja responde "o que entra em
     * cada farm".
     *
     * Nao precisa de ponto de passagem: no de ramo nunca ocupa o vao entre
     * colunas, entao a curva de uma faixa para outra passa pelo vao sem
     * encostar em card nenhum.
     */
    const emFaixas = useMemo(() => {
        if (!faixas.length) {
            return null;
        }

        // Blocos que fluem como texto.
        //
        // Uma etapa por bloco, e os blocos vao preenchendo a linha ate ela
        // encher; ai quebra para a de baixo. Empilhar um bloco por linha punha
        // vinte e um passos numa coluna de dois mil pixels de altura com a
        // largura toda vazia; assim o desenho fica quase quadrado e cabe na
        // tela sem afastar tudo.
        //
        // Dentro do bloco a posicao sai das ligacoes internas: quem depende de
        // alguem do mesmo bloco vai para a coluna seguinte, e irmaos empilham.
        // Bloco sem ligacao nenhuma por dentro vira uma linha so, que e o caso
        // dos jobs e dos admins: tres farms independentes lado a lado.

        const dentroDoBloco: { [chave: string]: string } = {};
        nos.forEach(no => { if (no.faixa) { dentroDoBloco[no.chave] = no.faixa; } });

        const blocos: Array<Bloco> = [];

        faixas.forEach(faixa => {
            const daFaixa = nos
                .filter(no => no.faixa === faixa.chave)
                .sort((a2, b2) => a2.nivel - b2.nivel || a2.numero - b2.numero);

            if (!daFaixa.length) {
                return;
            }

            const chaves = daFaixa.map(no => no.chave);
            const temLigacaoInterna = arestas.some(aresta =>
                chaves.indexOf(aresta.de) >= 0 && chaves.indexOf(aresta.para) >= 0);

            const coluna: { [chave: string]: number } = {};

            if (temLigacaoInterna) {
                // Profundidade dentro do bloco: a coluna de um passo e a do
                // ultimo de quem ele depende, mais um.
                daFaixa.forEach(no => {
                    let maior = -1;
                    no.depende.forEach(pai => {
                        if (dentroDoBloco[pai] === faixa.chave) {
                            maior = Math.max(maior, coluna[pai] === undefined ? 0 : coluna[pai]);
                        }
                    });
                    coluna[no.chave] = maior + 1;
                });
            } else {
                daFaixa.forEach((no, indice) => { coluna[no.chave] = indice; });
            }

            // A linha sai da corrente, e nao da ordem de leitura.
            //
            // Pegando a primeira linha livre da coluna, a corrente da Virginia
            // serpenteava: o copiar numa linha, o launch template noutra e o
            // refresh de volta na primeira. Aqui cada raiz do bloco recebe uma
            // faixa de linhas propria, e o filho fica na linha do pai quando ela
            // esta livre — corrente reta, e irmaos empilhados logo abaixo.
            const paisNoBloco = (no: InfraNo): Array<string> =>
                no.depende.filter(pai => dentroDoBloco[pai] === faixa.chave);

            const filhos: { [chave: string]: Array<InfraNo> } = {};
            daFaixa.forEach(no => {
                paisNoBloco(no).forEach(pai => {
                    filhos[pai] = (filhos[pai] || []).concat([ no ]);
                });
            });

            const linhaDe: { [chave: string]: number } = {};
            const ocupadas: { [coluna: number]: { [linha: number]: boolean } } = {};

            const reservar = (c: number, desejada: number): number => {
                let l = desejada;
                while (ocupadas[c] && ocupadas[c][l]) {
                    l++;
                }
                if (!ocupadas[c]) {
                    ocupadas[c] = {};
                }
                ocupadas[c][l] = true;
                return l;
            };

            let base = 0;
            daFaixa.forEach(raiz => {
                if (paisNoBloco(raiz).length || linhaDe[raiz.chave] !== undefined) {
                    return;
                }

                let maior = base;
                const descer = (no: InfraNo, desejada: number) => {
                    if (linhaDe[no.chave] !== undefined) {
                        return;
                    }
                    const l = reservar(coluna[no.chave], desejada);
                    linhaDe[no.chave] = l;
                    maior = Math.max(maior, l);
                    (filhos[no.chave] || []).forEach(filho => descer(filho, l));
                };

                descer(raiz, base);
                base = maior + 1;
            });

            // Sobra teorica: passo cujo pai esta no bloco mas nao foi visitado.
            daFaixa.forEach(no => {
                if (linhaDe[no.chave] === undefined) {
                    linhaDe[no.chave] = reservar(coluna[no.chave], base++);
                }
            });

            const posicionados = daFaixa.map(no => ({
                no,
                coluna: coluna[no.chave],
                linha: linhaDe[no.chave]
            }));

            blocos.push({
                chave: faixa.chave,
                rotulo: faixa.rotulo,
                ligado: faixa.ligado,
                nos: posicionados,
                colunas: Math.max.apply(null, posicionados.map(p => p.coluna)) + 1,
                linhas: Math.max.apply(null, posicionados.map(p => p.linha)) + 1
            });
        });

        const larguraDe = (colunas: number): number =>
            colunas * LARGURA_NO + (colunas - 1) * GAP_COLUNA + PAD_GRUPO * 2;
        const alturaDe = (linhas: number): number =>
            linhas * ALTURA_NO + (linhas - 1) * GAP_LINHA;

        // Distribui os blocos em linhas, quebrando quando o proximo nao cabe.
        const linhasDeBloco: Array<Array<Bloco>> = [];
        let atual: Array<Bloco> = [];
        let usado = 0;

        blocos.forEach(bloco => {
            const largura = larguraDe(bloco.colunas);
            if (atual.length && usado + GAP_GRUPO + largura > LARGURA_MAXIMA) {
                linhasDeBloco.push(atual);
                atual = [];
                usado = 0;
            }
            atual.push(bloco);
            usado += (usado ? GAP_GRUPO : 0) + largura;
        });
        if (atual.length) {
            linhasDeBloco.push(atual);
        }

        const posicoes: { [chave: string]: Posicao } = {};
        const quadros: Array<Quadro> = [];
        // Em que linha de bloco cada passo caiu, e o vao livre depois de cada
        // linha. E por esses vaos que as voltas andam.
        const linhaDoNo: { [chave: string]: number } = {};
        const corredoresDasLinhas: Array<{ topo: number; base: number }> = [];

        let y = MARGEM;

        linhasDeBloco.forEach((linha, indiceDaLinha) => {
            const alturaDaLinha = Math.max.apply(null, linha.map(b => alturaDe(b.linhas)));
            let x = MARGEM + CALHA;

            linha.forEach(bloco => {
                const topo = y + ALTURA_GRUPO;

                bloco.nos.forEach(item => {
                    linhaDoNo[item.no.chave] = indiceDaLinha;
                    posicoes[item.no.chave] = {
                        x: x + PAD_GRUPO + item.coluna * (LARGURA_NO + GAP_COLUNA),
                        y: topo + PAD_GRUPO + item.linha * (ALTURA_NO + GAP_LINHA)
                    };
                });

                quadros.push({
                    chave: bloco.chave,
                    rotulo: bloco.rotulo,
                    ligado: bloco.ligado,
                    x,
                    largura: larguraDe(bloco.colunas),
                    y: topo,
                    altura: alturaDaLinha + PAD_GRUPO * 2
                });

                x += larguraDe(bloco.colunas) + GAP_GRUPO;
            });

            const fimDaLinha = y + ALTURA_GRUPO + alturaDaLinha + PAD_GRUPO * 2;
            corredoresDasLinhas.push({
                topo: fimDaLinha,
                base: fimDaLinha + GAP_LINHA_BLOCO
            });

            y = fimDaLinha + GAP_LINHA_BLOCO;
        });

        const pontosDaAresta: { [chave: string]: Array<Posicao> } = {};

        const perto = (de: Posicao, para: Posicao): boolean =>
            para.x >= de.x + LARGURA_NO
            && para.x <= de.x + LARGURA_NO + GAP_COLUNA + PAD_GRUPO * 2 + GAP_GRUPO;

        const longas = arestas.filter(aresta =>
            posicoes[aresta.de] && posicoes[aresta.para]
            && !perto(posicoes[aresta.de], posicoes[aresta.para]));

        // Toda linha horizontal de uma volta anda num corredor: a faixa vazia
        // entre duas linhas de bloco. Saida e entrada dividem o mesmo corredor,
        // e por isso as raias sao contadas juntas — escalonar cada familia por
        // sua conta punha a saida de um bloco exatamente em cima da entrada do
        // seguinte, e as duas viravam um traco de mil pixels.
        interface Corredor {
            topo: number;
            base: number;
            quantas: number;
            usadas: number;
        }

        const corredores: Array<Corredor> = corredoresDasLinhas.map(faixa => ({
            topo: faixa.topo,
            base: faixa.base,
            quantas: 0,
            usadas: 0
        }));

        // Corredor de saida: o vao logo abaixo da linha de quem sai. Corredor
        // de entrada: o vao logo acima da linha de quem chega — que e o mesmo,
        // quando os dois estao na mesma linha e a volta so precisa de um vao.
        const corredorDaSaida = (chave: string): number => linhaDoNo[chave];
        const corredorDaEntrada = (de: string, para: string): number =>
            linhaDoNo[de] === linhaDoNo[para] ? linhaDoNo[de] : linhaDoNo[para] - 1;

        longas.forEach(aresta => {
            const cs = corredorDaSaida(aresta.de);
            const ce = corredorDaEntrada(aresta.de, aresta.para);

            if (corredores[cs]) {
                corredores[cs].quantas++;
            }
            // Mesmo corredor nos dois lados: a volta usa uma raia so, porque a
            // linha entra e sai pelo mesmo vao.
            if (ce !== cs && corredores[ce]) {
                corredores[ce].quantas++;
            }
        });

        // A raia cabe sempre: se o corredor ficar apertado, os fios se
        // aproximam em vez de vazarem para cima dos cards.
        const alturaDaRaia = (corredor: Corredor, indice: number): number => {
            const util = corredor.base - corredor.topo - 16;
            const passo = corredor.quantas > 1
                ? Math.min(ESPACO_FIO, util / (corredor.quantas - 1))
                : 0;
            const meio = (corredor.topo + corredor.base) / 2;

            return meio + (indice - (corredor.quantas - 1) / 2) * passo;
        };

        interface Medida {
            chave: string;
            saida: Posicao;
            entrada: Posicao;
            saidaX: number;
            entradaX: number;
            corredorSaida: number;
            corredorEntrada: number;
            topo: number;
            base: number;
            trilho: number;
        }

        const medidas: Array<Medida> = [];

        longas.forEach(aresta => {
            const de = posicoes[aresta.de];
            const para = posicoes[aresta.para];

            const iCorredorSaida = corredorDaSaida(aresta.de);
            const iCorredorEntrada = corredorDaEntrada(aresta.de, aresta.para);

            const saida = { x: de.x + LARGURA_NO, y: de.y + ALTURA_NO / 2 };
            const entrada = { x: para.x, y: para.y + ALTURA_NO / 2 };

            const raiaSaida = corredores[iCorredorSaida] ? corredores[iCorredorSaida].usadas++ : 0;
            const corredorSaida = corredores[iCorredorSaida]
                ? alturaDaRaia(corredores[iCorredorSaida], raiaSaida)
                : saida.y;

            const mesmoCorredor = iCorredorEntrada === iCorredorSaida;
            const raiaEntrada = mesmoCorredor
                ? raiaSaida
                : (corredores[iCorredorEntrada] ? corredores[iCorredorEntrada].usadas++ : 0);
            const corredorEntrada = mesmoCorredor
                ? corredorSaida
                : (corredores[iCorredorEntrada]
                    ? alturaDaRaia(corredores[iCorredorEntrada], raiaEntrada)
                    : entrada.y);

            // Os trechos verticais curtos abrem pela raia, e nao pela altura:
            // com a altura, duas raias vizinhas ficavam a tres pixels uma da
            // outra e voltavam a parecer um fio so.
            medidas.push({
                chave: aresta.de + '>' + aresta.para,
                saida,
                entrada,
                saidaX: saida.x + VOLTA_FOLGA + raiaSaida * 6,
                entradaX: entrada.x - PAD_GRUPO - VOLTA_FOLGA - raiaEntrada * 5,
                corredorSaida,
                corredorEntrada,
                topo: Math.min(corredorSaida, corredorEntrada),
                base: Math.max(corredorSaida, corredorEntrada),
                trilho: 0
            });
        });

        // Trilho por convivencia: duas arestas so dividem trilho quando os
        // trechos verticais delas nao se cruzam em altura nenhuma.
        const fimDoTrilho: Array<number> = [];
        medidas
            .slice()
            .sort((a2, b2) => a2.topo - b2.topo)
            .forEach(medida => {
                let escolhido = -1;
                for (let t = 0; t < fimDoTrilho.length; t++) {
                    if (fimDoTrilho[t] < medida.topo - ESPACO_FIO) {
                        escolhido = t;
                        break;
                    }
                }
                if (escolhido < 0) {
                    escolhido = fimDoTrilho.length;
                    fimDoTrilho.push(0);
                }
                fimDoTrilho[escolhido] = medida.base;
                medida.trilho = escolhido;
            });

        const cabem = Math.max(1, Math.floor((CALHA - VOLTA_FOLGA) / CALHA_PASSO));

        medidas.forEach(medida => {
            const trilhoX = MARGEM + (medida.trilho % cabem) * CALHA_PASSO;

            const pontos: Array<Posicao> = [
                medida.saida,
                { x: medida.saidaX, y: medida.saida.y },
                { x: medida.saidaX, y: medida.corredorSaida }
            ];

            // A calha so entra quando a volta troca de corredor.
            //
            // Com um corredor so, passar pela calha era ir ate a esquerda e
            // voltar pelo mesmo y: um pico de ida e volta, e o canto
            // arredondado numa inversao de 180 graus sai quebrado. Sem ela o
            // trecho horizontal vai direto de um lado ao outro, no mesmo vao.
            if (Math.abs(medida.corredorEntrada - medida.corredorSaida) > 1) {
                pontos.push(
                    { x: trilhoX, y: medida.corredorSaida },
                    { x: trilhoX, y: medida.corredorEntrada }
                );
            }

            pontos.push(
                { x: medida.entradaX, y: medida.corredorEntrada },
                { x: medida.entradaX, y: medida.entrada.y },
                medida.entrada
            );

            pontosDaAresta[medida.chave] = pontos;
        });

        arestas.forEach(aresta => {
            const de = posicoes[aresta.de];
            const para = posicoes[aresta.para];
            if (!de || !para || !perto(de, para)) {
                return;
            }
            pontosDaAresta[aresta.de + '>' + aresta.para] = [
                { x: de.x + LARGURA_NO, y: de.y + ALTURA_NO / 2 },
                { x: para.x, y: para.y + ALTURA_NO / 2 }
            ];
        });

        // O canvas fecha pelo desenho, e nao so pelos quadros: o corredor de
        // uma volta pode passar abaixo do ultimo bloco, e a saida avanca um
        // pouco a direita do ultimo card. Medindo pelos quadros, essa parte
        // ficava fora e a linha aparecia cortada.
        let direita = quadros.reduce(
            (maior, quadro) => Math.max(maior, quadro.x + quadro.largura), 0);
        let base = Math.max(0, y - GAP_LINHA_BLOCO);

        Object.keys(pontosDaAresta).forEach(chave => {
            pontosDaAresta[chave].forEach(ponto => {
                direita = Math.max(direita, ponto.x);
                base = Math.max(base, ponto.y);
            });
        });

        return {
            posicoes,
            pontosDaAresta,
            quadros,
            largura: direita + MARGEM,
            altura: base + MARGEM
        };
    }, [nos, arestas, faixas]);

    const empacotado = useMemo(() => {
        if (faixas.length) {
            return null;
        }

        const xDaColuna = (nivel: number): number => MARGEM + nivel * (LARGURA_NO + GAP_COLUNA);

        const nivelDe: { [chave: string]: number } = {};
        nos.forEach(no => { nivelDe[no.chave] = no.nivel; });

        // Aresta que pula coluna ganha um ponto de passagem em cada coluna do
        // meio. Esse ponto entra no empacotamento como se fosse um no, so que
        // sem altura: e o que impede a linha de cruzar um card, sem precisar
        // desviar por cima do desenho inteiro.
        interface Item {
            chave: string;
            nivel: number;
            alturaItem: number;
            pais: Array<string>;
            y: number;
            ordem: number;
        }

        const itens: { [chave: string]: Item } = {};
        let ordem = 0;

        nos.forEach(no => {
            itens[no.chave] = {
                chave: no.chave,
                nivel: no.nivel,
                alturaItem: ALTURA_NO,
                pais: [],
                y: 0,
                ordem: ordem++
            };
        });

        // Rota de cada aresta pelas colunas: origem, pontos de passagem, destino.
        const rotas: { [chave: string]: Array<string> } = {};

        arestas.forEach(aresta => {
            const nivelDeOrigem = nivelDe[aresta.de];
            const nivelDeDestino = nivelDe[aresta.para];
            if (nivelDeOrigem === undefined || nivelDeDestino === undefined) {
                return;
            }

            const rota = [aresta.de];
            let anterior = aresta.de;

            for (let nivel = nivelDeOrigem + 1; nivel < nivelDeDestino; nivel++) {
                const chave = '→' + aresta.de + '>' + aresta.para + '@' + nivel;
                itens[chave] = {
                    chave,
                    nivel,
                    alturaItem: 0,
                    pais: [anterior],
                    y: 0,
                    ordem: ordem++
                };
                rota.push(chave);
                anterior = chave;
            }

            itens[aresta.para].pais.push(anterior);
            rota.push(aresta.para);
            rotas[aresta.de + '>' + aresta.para] = rota;
        });

        const porNivel: { [nivel: number]: Array<Item> } = {};
        Object.keys(itens).forEach(chave => {
            const item = itens[chave];
            if (!porNivel[item.nivel]) {
                porNivel[item.nivel] = [];
            }
            porNivel[item.nivel].push(item);
        });

        const niveis = Object.keys(porNivel).map(Number).sort((a, b) => a - b);
        const centro = (item: Item): number => item.y + item.alturaItem / 2;

        niveis.forEach(nivel => {
            const coluna = porNivel[nivel];

            // Cada item quer ficar na altura media de quem o alimenta: filho
            // unico sai reto do pai, e um pai com varios filhos abre o leque em
            // torno da propria linha.
            const desejado: { [chave: string]: number } = {};
            coluna.forEach(item => {
                const pais = item.pais.filter(chave => itens[chave]);
                desejado[item.chave] = pais.length
                    ? pais.reduce((soma, chave) => soma + centro(itens[chave]), 0) / pais.length
                    : item.ordem * (ALTURA_NO + GAP_LINHA) + ALTURA_NO / 2;
            });

            coluna.sort((a, b) => {
                const diferenca = desejado[a.chave] - desejado[b.chave];
                return diferenca !== 0 ? diferenca : a.ordem - b.ordem;
            });

            // Coloca no ponto desejado e empurra para baixo so o necessario
            // para ninguem encostar. Entre dois fios a folga e menor que entre
            // dois cards, senao a coluna de passagem estica o desenho a toa.
            coluna.forEach((item, indice) => {
                item.y = desejado[item.chave] - item.alturaItem / 2;
                if (indice === 0) {
                    return;
                }
                const acima = coluna[indice - 1];
                const folga = acima.alturaItem && item.alturaItem ? GAP_LINHA : GAP_FIO;
                const minimo = acima.y + acima.alturaItem + folga;
                if (item.y < minimo) {
                    item.y = minimo;
                }
            });

            // Reequilibra a coluna inteira em torno da media que se queria, para
            // o empurrao nao puxar tudo para baixo.
            const media = coluna.reduce((soma, item) => soma + centro(item), 0) / coluna.length;
            const mediaDesejada = coluna.reduce((soma, item) => soma + desejado[item.chave], 0) / coluna.length;
            const ajuste = mediaDesejada - media;
            if (ajuste) {
                coluna.forEach(item => { item.y += ajuste; });
            }
        });

        const todos = Object.keys(itens).map(chave => itens[chave]);
        const topo = todos.reduce((menor, item) => Math.min(menor, item.y), Infinity);
        const deslocamento = MARGEM - (isFinite(topo) ? topo : 0);
        todos.forEach(item => { item.y += deslocamento; });

        const posicoes: { [chave: string]: Posicao } = {};
        nos.forEach(no => {
            posicoes[no.chave] = { x: xDaColuna(no.nivel), y: itens[no.chave].y };
        });

        // Pontos por onde a linha passa, ja em coordenadas de tela: sai da
        // direita do card, atravessa os pontos de passagem e entra pela
        // esquerda do destino.
        const pontosDaAresta: { [chave: string]: Array<Posicao> } = {};
        Object.keys(rotas).forEach(chave => {
            pontosDaAresta[chave] = rotas[chave].map((passo, indice, lista) => {
                const item = itens[passo];
                if (indice === 0) {
                    return { x: xDaColuna(item.nivel) + LARGURA_NO, y: centro(item) };
                }
                if (indice === lista.length - 1) {
                    return { x: xDaColuna(item.nivel), y: centro(item) };
                }
                return { x: xDaColuna(item.nivel) + LARGURA_NO / 2, y: centro(item) };
            });
        });

        const ultimoNivel = niveis.length ? niveis[niveis.length - 1] : 0;
        const base = todos.reduce((maior, item) => Math.max(maior, item.y + item.alturaItem), 0);

        return {
            posicoes,
            pontosDaAresta,
            quadros: [] as Array<Quadro>,
            largura: MARGEM * 2 + (ultimoNivel + 1) * LARGURA_NO + ultimoNivel * GAP_COLUNA,
            altura: base + MARGEM
        };
    }, [nos, arestas, faixas]);

    const { posicoes, pontosDaAresta, quadros, largura, altura } =
        emFaixas || empacotado || {
            posicoes: {} as { [chave: string]: Posicao },
            pontosDaAresta: {} as { [chave: string]: Array<Posicao> },
            quadros: [] as Array<Quadro>,
            largura: 0,
            altura: 0
        };

    /** Enquadra o DAG inteiro e centraliza. */
    const ajustar = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport || largura === 0 || altura === 0) {
            return;
        }

        const caixa = viewport.getBoundingClientRect();
        if (caixa.width === 0 || caixa.height === 0) {
            return;
        }

        const escala = limitar(
            Math.min(caixa.width / largura, caixa.height / altura, 1),
            ZOOM_MIN,
            1
        );

        setVista({
            x: (caixa.width - largura * escala) / 2,
            y: (caixa.height - altura * escala) / 2,
            z: escala
        });
    }, [largura, altura]);

    useEffect(() => { ajustar(); }, [ajustar]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observador = new ResizeObserver(() => ajustar());
        observador.observe(viewport);
        return () => observador.disconnect();
    }, [ajustar]);

    const aoPressionar = (evento: React.MouseEvent) => {
        if (evento.button !== 0 && evento.button !== 1) {
            return;
        }
        inicioRef.current = {
            mouseX: evento.clientX,
            mouseY: evento.clientY,
            panX: vista.x,
            panY: vista.y
        };
        arrastouRef.current = false;
        setArrastando(true);
    };

    useEffect(() => {
        if (!arrastando) {
            return;
        }

        const mover = (evento: MouseEvent) => {
            const inicio = inicioRef.current;
            if (!inicio) {
                return;
            }
            const dx = evento.clientX - inicio.mouseX;
            const dy = evento.clientY - inicio.mouseY;

            if (Math.abs(dx) > ARRASTO_MINIMO || Math.abs(dy) > ARRASTO_MINIMO) {
                arrastouRef.current = true;
            }

            setVista(atual => ({ ...atual, x: inicio.panX + dx, y: inicio.panY + dy }));
        };

        const soltar = () => {
            inicioRef.current = null;
            setArrastando(false);
        };

        window.addEventListener('mousemove', mover);
        window.addEventListener('mouseup', soltar);
        return () => {
            window.removeEventListener('mousemove', mover);
            window.removeEventListener('mouseup', soltar);
        };
    }, [arrastando]);

    // Listener nativo com passive: false. O onWheel do React e passivo, entao o
    // preventDefault nao vale e o ctrl+roda vira zoom da pagina inteira.
    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }

        const aoRolar = (evento: WheelEvent) => {
            evento.preventDefault();

            // Roda e zoom, sem tecla nenhuma. Rolar um canvas de fluxo nao faz
            // sentido: nao existe "fim da pagina" para chegar, e quem quer mover
            // arrasta. Shift continua deslocando na horizontal, que e o gesto
            // esperado de quem tem trackpad.
            if (evento.shiftKey) {
                setVista(atual => ({
                    ...atual,
                    x: atual.x - (evento.deltaX || evento.deltaY)
                }));
                return;
            }

            const caixa = viewport.getBoundingClientRect();
            const cursorX = evento.clientX - caixa.left;
            const cursorY = evento.clientY - caixa.top;

            setVista(atual => {
                // O passo acompanha o quanto a roda girou: trackpad manda
                // deltas pequenos e o zoom precisa ser suave, mouse manda
                // deltas grandes de uma vez.
                const intensidade = Math.min(Math.abs(evento.deltaY) / 100, 1) * 0.12;
                const z = limitar(
                    atual.z * (evento.deltaY < 0 ? 1 + intensidade : 1 / (1 + intensidade)),
                    ZOOM_MIN,
                    ZOOM_MAX
                );
                const fator = z / atual.z;

                // O ponto sob o cursor fica onde esta: e o que faz o zoom
                // parecer que aproxima o desenho, e nao que empurra a tela.
                return {
                    z,
                    x: cursorX - (cursorX - atual.x) * fator,
                    y: cursorY - (cursorY - atual.y) * fator
                };
            });
        };

        viewport.addEventListener('wheel', aoRolar, { passive: false });
        return () => viewport.removeEventListener('wheel', aoRolar);
    }, []);

    const aplicarZoom = (fator: number) => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        const caixa = viewport.getBoundingClientRect();
        const centroX = caixa.width / 2;
        const centroY = caixa.height / 2;

        setVista(atual => {
            const z = limitar(atual.z * fator, ZOOM_MIN, ZOOM_MAX);
            const proporcao = z / atual.z;
            return {
                z,
                x: centroX - (centroX - atual.x) * proporcao,
                y: centroY - (centroY - atual.y) * proporcao
            };
        });
    };

    const aoClicarNo = (chave: string) => {
        if (arrastouRef.current) {
            return;
        }
        onSelecionar(chave);
    };

    const classeDoNo = (no: InfraNo): string => {
        const classes = ['infra-no', 'infra-no-' + no.destino];
        switch (no.situacao) {
            case STEP_CONCLUIDO: classes.push('infra-no-concluido'); break;
            case STEP_ERRO: classes.push('infra-no-erro'); break;
            case STEP_EXECUTANDO: classes.push('infra-no-executando'); break;
            case STEP_PULADO: classes.push('infra-no-pulado'); break;
            default: classes.push(no.liberado ? 'infra-no-liberado' : 'infra-no-pendente');
        }
        if (selecionado === no.chave) {
            classes.push('infra-no-selecionado');
        }
        if (no.ligado === false) {
            classes.push('infra-no-desligado');
        }
        return classes.join(' ');
    };

    const marcador = (no: InfraNo): string => {
        switch (no.situacao) {
            case STEP_CONCLUIDO: return '✓';
            case STEP_ERRO: return '✕';
            case STEP_EXECUTANDO: return '…';
            case STEP_PULADO: return '⤼';
            default: return no.liberado ? '▶' : '·';
        }
    };

    const situacaoPorChave = useMemo(() => {
        const mapa: { [chave: string]: number } = {};
        nos.forEach(no => { mapa[no.chave] = no.situacao; });
        return mapa;
    }, [nos]);

    /**
     * Uma bezier por trecho, sempre com as alcas na horizontal. E o que da a
     * leitura de fluxo: a linha sai reta do card, faz a curva no meio do vao e
     * chega reta no proximo, sem canto e sem faixa de desvio.
     */
    const caminhoAresta = (pontos: Array<Posicao>): string => {
        if (pontos.length < 2) {
            return '';
        }

        // Dois pontos e um salto curto na mesma faixa: bezier, que da a leitura
        // de fluxo saindo reto do card e chegando reto no proximo.
        if (pontos.length === 2) {
            const [de, para] = pontos;
            const alca = Math.max(24, (para.x - de.x) / 2);

            return 'M ' + de.x + ' ' + de.y
                + ' C ' + (de.x + alca) + ' ' + de.y
                + ', ' + (para.x - alca) + ' ' + para.y
                + ', ' + para.x + ' ' + para.y;
        }

        // Mais pontos e a volta pela calha: trechos retos com o canto cortado
        // por uma quadratica. Curva em tudo viraria espaguete num percurso de
        // seis trechos; canto vivo pareceria diagrama de rede.
        let caminho = 'M ' + pontos[0].x + ' ' + pontos[0].y;

        for (let i = 1; i < pontos.length - 1; i++) {
            const anterior = pontos[i - 1];
            const canto = pontos[i];
            const proximo = pontos[i + 1];

            const entradaComprimento = Math.hypot(canto.x - anterior.x, canto.y - anterior.y);
            const saidaComprimento = Math.hypot(proximo.x - canto.x, proximo.y - canto.y);
            const raio = Math.min(VOLTA_RAIO, entradaComprimento / 2, saidaComprimento / 2);

            // Ida e volta pelo mesmo eixo nao tem canto para arredondar: a
            // quadratica ali devolve um traco torto. Nao deve acontecer, mas
            // desenhar errado e pior que desenhar reto.
            const inverteu =
                Math.sign(canto.x - anterior.x) === -Math.sign(proximo.x - canto.x)
                && Math.abs(canto.y - anterior.y) < 1 && Math.abs(proximo.y - canto.y) < 1;

            if (raio < 1 || inverteu) {
                caminho += ' L ' + canto.x + ' ' + canto.y;
                continue;
            }

            const antes = {
                x: canto.x - ((canto.x - anterior.x) / entradaComprimento) * raio,
                y: canto.y - ((canto.y - anterior.y) / entradaComprimento) * raio
            };
            const depois = {
                x: canto.x + ((proximo.x - canto.x) / saidaComprimento) * raio,
                y: canto.y + ((proximo.y - canto.y) / saidaComprimento) * raio
            };

            caminho += ' L ' + antes.x.toFixed(1) + ' ' + antes.y.toFixed(1)
                + ' Q ' + canto.x + ' ' + canto.y
                + ', ' + depois.x.toFixed(1) + ' ' + depois.y.toFixed(1);
        }

        const ultimo = pontos[pontos.length - 1];
        caminho += ' L ' + ultimo.x + ' ' + ultimo.y;

        return caminho;
    };

    return (
        <div
            ref={viewportRef}
            className={'infra-flow-viewport' + (arrastando ? ' infra-flow-arrastando' : '')}
            onMouseDown={aoPressionar}
        >
            <div
                className="infra-flow-canvas"
                style={{
                    width: largura,
                    height: altura,
                    transform: 'translate(' + vista.x + 'px, ' + vista.y + 'px) scale(' + vista.z + ')'
                }}
            >
                {quadros.map(quadro => (
                    <div
                        key={quadro.chave}
                        className={'infra-quadro' + (quadro.ligado ? '' : ' infra-quadro-desligado')}
                        style={{
                            left: quadro.x,
                            top: quadro.y,
                            width: quadro.largura,
                            height: quadro.altura
                        }}
                    >
                        <span className="infra-quadro-nome">{quadro.rotulo}</span>
                    </div>
                ))}


                <svg className="infra-flow-arestas" width={largura} height={altura}>
                    {arestas.map((aresta, indice) => {
                        const pontos = pontosDaAresta[aresta.de + '>' + aresta.para];
                        if (!pontos) {
                            return null;
                        }
                        const origemPronta = situacaoPorChave[aresta.de] === STEP_CONCLUIDO
                            || situacaoPorChave[aresta.de] === STEP_PULADO;

                        return (
                            <path
                                key={indice}
                                d={caminhoAresta(pontos)}
                                className={'infra-aresta' + (origemPronta ? ' infra-aresta-ativa' : '')}
                            />
                        );
                    })}
                </svg>

                {nos.map(no => {
                    const posicao = posicoes[no.chave];
                    if (!posicao) {
                        return null;
                    }
                    return (
                        <div
                            key={no.chave}
                            className={classeDoNo(no)}
                            style={{ left: posicao.x, top: posicao.y, width: LARGURA_NO, height: ALTURA_NO }}
                            onClick={() => aoClicarNo(no.chave)}
                            title={no.resumo}
                        >
                            {/* O passo rodando ganha uma camada por cima com o
                                loader grande: e o que o olho procura quando
                                volta para a tela. A camada nao intercepta
                                clique, entao o card continua abrindo o log. */}
                            {no.situacao === STEP_EXECUTANDO && (
                                <div className="infra-no-cobertura">
                                    <span className="infra-no-girando-grande" />
                                </div>
                            )}

                            <div className="infra-no-topo">
                                <span className="infra-no-marcador">{marcador(no)}</span>
                                <span className="infra-no-numero">{no.numero}</span>
                                <span className="infra-no-destino">{rotuloDestino(no.destino)}</span>
                                {no.depende.length > 1 && (
                                    <span className="infra-no-juncao" title={'Espera ' + no.depende.length + ' passos'}>
                                        {no.depende.length} ⇉
                                    </span>
                                )}
                            </div>
                            <div className="infra-no-rotulo">{no.rotulo}</div>
                            <div className="infra-no-pe">
                                {no.ligado === false
                                    ? 'fora deste deploy'
                                    : no.run && no.run.ms !== null
                                        ? duracao(no.run.ms) + (no.run.numero > 1 ? ' · #' + no.run.numero : '')
                                        : (no.liberado ? 'pronto para rodar' : no.bloqueio || 'aguardando')}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="infra-flow-controles" onMouseDown={evento => evento.stopPropagation()}>
                <button type="button" title="Afastar" onClick={() => aplicarZoom(1 / 1.2)}>−</button>
                <span className="infra-flow-zoom">{Math.round(vista.z * 100)}%</span>
                <button type="button" title="Aproximar" onClick={() => aplicarZoom(1.2)}>+</button>
                <button type="button" className="infra-flow-ajustar" title="Enquadrar o fluxo" onClick={ajustar}>
                    Ajustar
                </button>
            </div>

            <div className="infra-flow-dica">arraste para mover · roda para zoom</div>
        </div>
    );
};

export default FlowGraph;
